import { useEffect, useId, useState } from "react";
import { useRouter } from "next/router";
import { Box, Button, Dialog, IconButton, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useAtomValue } from "jotai";
import { backgroundColorState, ortexColorState, textColorState } from "@/lib/store";
import { withAlpha } from "@/lib/options-platform/theme";
import { postTutorialImpression } from "@/controllers/tutorials";
import { useTour } from "@/components/tour/TourGuide";

/**
 * DB-driven promo modal — the `kind="promo"` face of the on-screen
 * messaging system (screening.Tutorial). Content, CTA, audience
 * targeting, scheduling, pause switch and impression stats all live in
 * Django admin; shipping a new campaign needs no frontend deploy.
 *
 * Reuses `useTour` wholesale: the server decides whether THIS user gets
 * the promo (targeting fails closed), auto-open fires once per user, and
 * any dismissal is persisted (settings API + localStorage). A promo that
 * was dismissed stays dismissed — re-campaigns should use a fresh
 * tour_id so the audience resets deliberately.
 *
 * Replaces the hardcoded TryNewBanner ("the new Options Platform is
 * here" popup) — same hero styling, admin-controlled content.
 */
interface Props {
    tourId: string;
    /** Override the DB CTA target (e.g. per-ticker deep links). */
    ctaHrefOverride?: string;
    /** In-app CTA action — used when the DB cta_href is an in-app trigger
     *  (convention: hrefs starting with "#", e.g. "#options-platform-
     *  subscribe"). Wins over navigation. */
    onCtaAction?: () => void;
    /** Fired when the user dismisses WITHOUT taking the CTA ("Maybe
     *  later", backdrop, ✕) — lets hosts run a declined follow-up. */
    onDismissed?: () => void;
    /** Paywall mode: the modal shows on EVERY visit while the server
     *  still targets this user (closing hides it for this page view
     *  only — nothing is persisted, and previously-stored dismissals
     *  are ignored). Buying removes the user from the audience via the
     *  Tutorial's exclude_groups, which is what finally stops it. */
    paywall?: boolean;
}

const PromoMessage = ({ tourId, ctaHrefOverride, onCtaAction, onDismissed, paywall = false }: Props) => {
    const router = useRouter();
    const accent = useAtomValue(ortexColorState) || "#0891B2";
    const background = useAtomValue(backgroundColorState);
    const textColor = useAtomValue(textColorState);
    const tour = useTour(tourId);
    // The heading names the dialog (aria-dialog-name); MUI's own id expects a DialogTitle, which this has not.
    const titleId = useId();
    const step = tour.steps[0];
    // Paywall mode forces open whenever the server serves steps —
    // stored dismissal flags don't apply. closedThisView makes the ✕ /
    // "Maybe later" work for the current page view only.
    const [closedThisView, setClosedThisView] = useState(false);
    const open = !!step && !closedThisView && (tour.open || paywall);

    useEffect(() => {
        if (open && step) postTutorialImpression(tourId, step.order);
    }, [open, step, tourId]);

    if (!open || !step) return null;

    // Promos always persist on close — a marketing popup must never
    // re-nag someone who already said no.
    const dismiss = (declined = true) => {
        if (paywall) {
            // Hide for THIS page view only — never persist a paywall
            // dismissal; it returns on the next options-page visit.
            setClosedThisView(true);
            tour.props.onClose(false);
        } else {
            tour.props.onClose(true);
        }
        if (declined) onDismissed?.();
    };
    const ctaHref = ctaHrefOverride ?? step.cta?.href ?? null;
    const ctaIsAction = !!onCtaAction && (!ctaHref || ctaHref.startsWith("#"));

    return (
        <Dialog
            open
            aria-labelledby={titleId}
            onClose={() => dismiss()}
            maxWidth="sm"
            fullWidth
            slotProps={{
                paper: {
                    sx: {
                        position: "relative",
                        backgroundColor: background,
                        border: `1px solid ${withAlpha(accent, 0.6)}`,
                        borderRadius: 2,
                        color: textColor,
                        overflow: "hidden",
                    },
                },
            }}
        >
            <IconButton size="small" aria-label="Dismiss" onClick={() => dismiss()} sx={{ position: "absolute", top: 12, right: 12, zIndex: 1, color: textColor, opacity: 0.6, "&:hover": { opacity: 1 } }}>
                <CloseIcon fontSize="small" />
            </IconButton>

            <Box
                sx={{
                    px: { xs: 2.5, sm: 4 },
                    pt: 4,
                    pb: 3,
                    background: `linear-gradient(160deg, ${withAlpha(accent, 0.18)} 0%, ${withAlpha(accent, 0.04)} 70%, transparent 100%)`,
                }}
            >
                <Typography component="h2" id={titleId} sx={{ fontSize: { xs: 22, sm: 26 }, fontWeight: 800, lineHeight: 1.2, letterSpacing: -0.3, pr: 4 }}>
                    {step.title}
                </Typography>
                <Typography
                    component="div"
                    variant="body1"
                    sx={{
                        mt: 1.5,
                        opacity: 0.92,
                        "& p": { m: 0, mb: 1.25 },
                        "& ul": { m: 0, mb: 1.25, pl: 2.5 },
                        "& li": { mb: 0.5 },
                    }}
                    dangerouslySetInnerHTML={{ __html: step.body }}
                />
                <Box sx={{ display: "flex", alignItems: "center", gap: 2, mt: 2, flexWrap: "wrap" }}>
                    {(ctaHref || ctaIsAction) && step.cta?.label && (
                        <Button
                            variant="contained"
                            size="large"
                            onClick={() => {
                                dismiss(false);
                                if (ctaIsAction) onCtaAction?.();
                                else if (ctaHref) router.push(ctaHref);
                            }}
                            sx={{
                                backgroundColor: accent,
                                color: background,
                                fontWeight: 700,
                                px: 3,
                                whiteSpace: "nowrap",
                                "&:hover": { backgroundColor: accent, filter: "brightness(1.08)" },
                            }}
                        >
                            {step.cta.label}
                        </Button>
                    )}
                    <Button variant="text" onClick={() => dismiss()} sx={{ color: textColor, opacity: 0.7, whiteSpace: "nowrap", "&:hover": { opacity: 1, background: "transparent" } }}>
                        Maybe later
                    </Button>
                </Box>
            </Box>
        </Dialog>
    );
};

export default PromoMessage;
